import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppStateFacade } from '@app-state';
import {
    BulkOperationResponse,
    IQueueItem,
    IQueueItemDto,
    IQueueItemIdentifier,
    IUser,
    ODataResponse,
    UserType,
} from '@models';
import { ODataQueryOptions } from '@services/generics/odata.query';
import { ODataService } from '@services/generics/odata.service';
import { MapperService } from '@services/mapper.service';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class QueueItemService extends ODataService<IQueueItem, number> {
    protected _endpoint: string;
    constructor(
        http: HttpClient,
        { authConfig }: AppStateFacade,
    ) {
        super(http, MapperService.queueItemMap);
        this._endpoint = `${authConfig.odataBaseUrl}/QueueItems`;
    }

    public getQueueItemHistory(
        itemId: number, queryOptions: ODataQueryOptions,
    ): Observable<ODataResponse<IQueueItem>> {
        if (queryOptions.expand instanceof Array) {
            queryOptions.expand.push('Robot');
        } else {
            queryOptions.expand = ['Robot'];
        }

        return this.get(queryOptions, `${this._endpoint}(${itemId})/UiPathODataSvc.GetItemProcessingHistory`);
    }

    public retryItems(itemIdentifiers: Array<IQueueItemIdentifier>): Observable<BulkOperationResponse> {
        const payload: string = JSON.stringify({
            'queueItems': itemIdentifiers.map(qi => this._toPoco(qi, MapperService.queueItemIdentifierMap)),
        });

        return this._http.post(`${this._endpoint}/UiPathODataSvc.RetryQueueItem`, payload)
            .pipe(
                map(res => this._mapData(res, MapperService.bulkOperationMap) as BulkOperationResponse),
            );
    }

    public getItemLastRetry(itemId: number, expand: Array<string> | null = null) {
        const expansionList = expand === null ? '' : '?$expand=' + expand.join(',');
        return this._http
            .get(`${this._endpoint}(${itemId})/UiPath.Server.Configuration.OData.GetItemLastRetry()${expansionList}`, {})
            .pipe(
                map((response) => this._mapData(response)),
            ) as Observable<IQueueItem>;
    }

    public changeItemReviewStatus(itemIdentifiers: Array<IQueueItemIdentifier>, status: string) {
        const payload: string = JSON.stringify({
            'queueItems': itemIdentifiers.map(qi => this._toPoco(<any>qi, MapperService.queueItemIdentifierMap)),
            'status': status,
        });

        return this._http.post(`${this._endpoint}/UiPathODataSvc.SetItemReviewStatus`, payload)
            .pipe(
                map(res => this._mapData<BulkOperationResponse>(res, MapperService.bulkOperationMap)),
            ) as Observable<BulkOperationResponse>;
    }

    public changeItemReviewer(itemIdentifiers: Array<IQueueItemIdentifier>, userId: number | null) {
        let payload: string;
        let action: string;

        if (userId) {
            action = 'UiPathODataSvc.SetItemReviewer';
            payload = JSON.stringify({
                'queueItems': itemIdentifiers.map(qi => this._toPoco(<any>qi, MapperService.queueItemIdentifierMap)),
                'userId': userId,
            });
        } else {
            action = 'UiPathODataSvc.UnsetItemReviewer';
            payload = JSON.stringify({
                'queueItems': itemIdentifiers.map(qi => this._toPoco(<any>qi, MapperService.queueItemIdentifierMap)),
            });
        }

        return this._http.post(`${this._endpoint}/${action}`, payload)
            .pipe(
                map(res => this._mapData<BulkOperationResponse>(res, MapperService.bulkOperationMap)),
            ) as Observable<BulkOperationResponse>;
    }

    public deleteBulk(itemIdentifiers: Array<IQueueItemIdentifier>) {
        const payload: string = JSON.stringify({
            'queueItems': itemIdentifiers.map(qi => this._toPoco(qi, MapperService.queueItemIdentifierMap)),
        });

        return this._http.post(`${this._endpoint}/UiPathODataSvc.DeleteBulk`, payload)
            .pipe(
                map(res => this._mapData<BulkOperationResponse>(res, MapperService.bulkOperationMap)),
            ) as Observable<BulkOperationResponse>;
    }

    public suggestReviewers = (searchTerm: string = '', fetchSize = 20, skip: number = 0) => {
        const queryOptions: ODataQueryOptions = {
            orderBy: ['UserName'],
            top: fetchSize,
            skip,
            filter: {
                UserName: {
                    startswith: searchTerm,
                },
                or: [
                    { Type: UserType[UserType.User] },
                    { Type: UserType[UserType.DirectoryUser] },
                ],
            },
        };

        const endpoint = this._endpoint + `/UiPath.Server.Configuration.OData.GetReviewers()`;

        return this._http
            .get(`${endpoint}${this.getQueryString(queryOptions)}`)
            .pipe(
                map(response => this._mapData(response, MapperService.userMap) as ODataResponse<IUser>),
                map(
                    ({ data, total }) =>
                        ({
                            total,
                            data: data.map(user => ({
                                id: user.id,
                                text: user.userName,
                            })),
                        }),
                ),
            );
    }

    public addQueueItem(item: IQueueItemDto) {
        const payload = {
            itemData: this._toPoco(item, MapperService.queueItemDtoMap),
        };

        return this._http.post(
            '/odata/Queues/UiPathODataSvc.AddQueueItem',
            JSON.stringify(payload),
        );
    }

    public updateQueueItem(key: number, item: IQueueItemDto) {
        const serverData = this._toPoco(item, MapperService.queueItemDtoMap);
        const content = JSON.stringify(serverData);

        return this._http
            .put(`${this._endpoint}(${key})`, content, {
                headers: this._jsonHeaders,
            });
    }
}
